import { useState, useRef } from 'react';
import { Camera, Loader2, X, User } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { queryKeys } from '../lib/queryKeys';

interface AvatarUploadProps {
  currentAvatarUrl: string | null;
  onUploadComplete: (url: string) => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const API_URL = import.meta.env.VITE_API_URL || 'https://api.filmple.io';

export function AvatarUpload({ currentAvatarUrl, onUploadComplete }: AvatarUploadProps) {
  const { session, user, profile } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Please select a JPEG, PNG, or WebP image');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError('Image must be less than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setPreview(event.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Resize and upload
    await resizeAndUpload(file);
  };

  const resizeAndUpload = async (file: File) => {
    if (!session?.access_token) {
      setError('You must be logged in to upload an avatar');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // Resize image client-side
      const resizedBlob = await resizeImage(file, 256, 256);

      // Upload to worker
      const formData = new FormData();
      formData.append('avatar', resizedBlob, 'avatar.jpg');

      const response = await fetch(`${API_URL}/api/upload/avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Upload failed');
      }

      const data = await response.json();
      onUploadComplete(data.url);
      setPreview(null);

      // Invalidate caches that contain user avatar URLs
      // Search results (user avatars from Meilisearch)
      queryClient.invalidateQueries({ queryKey: ['search'] });
      // Recent searches (user recents with stale avatar)
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.user.recentSearches(user.id) });
      }
      // Profile page cache
      if (profile?.username) {
        queryClient.invalidateQueries({ queryKey: queryKeys.profile.byUsername(profile.username.toLowerCase()) });
      }
    } catch (err) {
      console.error('Avatar upload error:', err);
      setError(err instanceof Error ? err.message : 'Upload failed');
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const resizeImage = (file: File, maxWidth: number, maxHeight: number): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        // Calculate dimensions maintaining aspect ratio
        let width = img.width;
        let height = img.height;

        // Crop to square (center crop)
        const minDimension = Math.min(width, height);
        const cropX = (width - minDimension) / 2;
        const cropY = (height - minDimension) / 2;

        // Create canvas
        const canvas = document.createElement('canvas');
        canvas.width = maxWidth;
        canvas.height = maxHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Draw cropped and resized image
        ctx.drawImage(
          img,
          cropX, cropY, minDimension, minDimension, // Source (crop)
          0, 0, maxWidth, maxHeight // Destination (resize)
        );

        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Could not convert canvas to blob'));
            }
          },
          'image/jpeg',
          0.9 // Quality
        );
      };

      img.onerror = () => reject(new Error('Could not load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  const clearPreview = () => {
    setPreview(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const displayUrl = preview || currentAvatarUrl;

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Avatar display */}
      <div className="relative">
        <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-teal-400 to-orange-500 flex items-center justify-center">
          {displayUrl ? (
            <img
              src={displayUrl}
              alt="Avatar"
              className="w-full h-full object-cover"
            />
          ) : (
            <User size={40} className="text-white" />
          )}
        </div>

        {/* Upload button overlay */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="absolute bottom-0 right-0 p-2 bg-[#1A1F26] border border-slate-600 rounded-full hover:bg-slate-700 transition-colors disabled:opacity-50"
        >
          {uploading ? (
            <Loader2 size={16} className="text-white animate-spin" />
          ) : (
            <Camera size={16} className="text-white" />
          )}
        </button>

        {/* Clear preview button */}
        {preview && !uploading && (
          <button
            type="button"
            onClick={clearPreview}
            className="absolute top-0 right-0 p-1 bg-red-500 rounded-full hover:bg-red-600 transition-colors"
          >
            <X size={12} className="text-white" />
          </button>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Status text */}
      {uploading && (
        <p className="text-sm text-slate-400">Uploading...</p>
      )}

      {/* Error message */}
      {error && (
        <p className="text-sm text-red-400">{error}</p>
      )}

      {/* Help text */}
      {!uploading && !error && (
        <p className="text-xs text-slate-500">Click camera to upload (max 5MB)</p>
      )}
    </div>
  );
}
